import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { LuChevronDown, LuMenu, LuX } from "react-icons/lu";
import BrandMark from "./BrandMark";
import QrifyMark from "./QrifyMark";
import PrimaryButton from "./PrimaryButton";

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About us" },
];

const SiteNav = () => {
  const [open, setOpen] = React.useState(false);
  const [productOpen, setProductOpen] = React.useState(false);
  const [drawerProductsOpen, setDrawerProductsOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const reduce = useReducedMotion();

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  React.useEffect(() => {
    setOpen(false);
    setProductOpen(false);
    setDrawerProductsOpen(false);
  }, [pathname]);

  const goTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  };

  return (
    <>
      <header
        className={`sv-nav${isHome ? " sv-nav--home" : ""}${
          scrolled ? " sv-nav--scrolled" : ""
        }`}
      >
        <div className="sv-nav__inner">
          <BrandMark
            brand={isHome ? "alegion" : "svasth"}
            size={isHome ? "xl" : "md"}
          />

          <ul className="sv-nav__links">
            {links.map((link) => (
              <li key={link.to}>
                <NavLink to={link.to} end={link.end} onClick={goTop}>
                  {({ isActive }) => (
                    <span className="sv-nav__link-wrap">
                      {link.label}
                      {isActive ? (
                        <motion.span
                          className="sv-nav__underline"
                          layoutId={reduce ? undefined : "nav-underline"}
                          transition={{
                            type: "spring",
                            stiffness: 380,
                            damping: 32,
                          }}
                        />
                      ) : null}
                    </span>
                  )}
                </NavLink>
              </li>
            ))}
            <li
              className={`sv-nav__dropdown${productOpen ? " is-open" : ""}`}
              onMouseEnter={() => setProductOpen(true)}
              onMouseLeave={() => setProductOpen(false)}
              onFocus={() => setProductOpen(true)}
              onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                  setProductOpen(false);
                }
              }}
            >
              <div className="sv-nav__dropdown-head">
                <NavLink to="/product" onClick={goTop}>
                  {({ isActive }) => (
                    <span className="sv-nav__link-wrap">
                      Products
                      {isActive ? (
                        <motion.span
                          className="sv-nav__underline"
                          layoutId={reduce ? undefined : "nav-underline"}
                          transition={{
                            type: "spring",
                            stiffness: 380,
                            damping: 32,
                          }}
                        />
                      ) : null}
                    </span>
                  )}
                </NavLink>
                <button
                  type="button"
                  className="sv-nav__dropdown-toggle"
                  aria-label="Toggle products menu"
                  aria-expanded={productOpen}
                  onClick={() => setProductOpen((current) => !current)}
                >
                  <LuChevronDown
                    className={`sv-nav__chevron${productOpen ? " is-open" : ""}`}
                    size={15}
                  />
                </button>
              </div>
              <AnimatePresence>
                {productOpen ? (
                  <motion.ul
                    className="sv-nav__dropdown-menu"
                    initial={reduce ? false : { opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={reduce ? undefined : { opacity: 0, y: -6 }}
                    transition={{ duration: 0.18 }}
                  >
                    <li>
                      <NavLink
                        to="/product"
                        onClick={() => {
                          goTop();
                          setProductOpen(false);
                        }}
                      >
                        <QrifyMark />
                      </NavLink>
                    </li>
                  </motion.ul>
                ) : null}
              </AnimatePresence>
            </li>
            <li>
              <PrimaryButton to="/contact">Contact us</PrimaryButton>
            </li>
          </ul>

          <button
            type="button"
            className="sv-nav__menu"
            aria-label="menu"
            aria-expanded={open}
            onClick={() => setOpen(true)}
          >
            <LuMenu size={24} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open ? (
          <motion.div
            className="sv-drawer is-open"
            onClick={() => setOpen(false)}
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={reduce ? undefined : { opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <motion.div
              className="sv-drawer__panel"
              onClick={(e) => e.stopPropagation()}
              initial={reduce ? false : { x: "100%" }}
              animate={{ x: 0 }}
              exit={reduce ? undefined : { x: "100%" }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              <button
                type="button"
                className="sv-nav__menu"
                aria-label="Close menu"
                onClick={() => setOpen(false)}
                style={{ alignSelf: "flex-end" }}
              >
                <LuX size={24} />
              </button>
              {links.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={reduce ? false : { opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.05, duration: 0.3 }}
                >
                  <NavLink
                    to={link.to}
                    end={link.end}
                    onClick={() => {
                      goTop();
                      setOpen(false);
                    }}
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                className="sv-drawer__group"
                initial={reduce ? false : { opacity: 0, x: 16 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15, duration: 0.3 }}
              >
                <div className="sv-drawer__product-head">
                  <NavLink
                    to="/product"
                    onClick={() => {
                      goTop();
                      setOpen(false);
                    }}
                  >
                    Products
                  </NavLink>
                  <button
                    type="button"
                    className="sv-drawer__product-toggle"
                    aria-label="Toggle products menu"
                    aria-expanded={drawerProductsOpen}
                    onClick={() =>
                      setDrawerProductsOpen((current) => !current)
                    }
                  >
                    <LuChevronDown
                      className={`sv-nav__chevron${
                        drawerProductsOpen ? " is-open" : ""
                      }`}
                      size={18}
                    />
                  </button>
                </div>
                <AnimatePresence initial={false}>
                  {drawerProductsOpen ? (
                    <motion.div
                      className="sv-drawer__sublinks"
                      initial={reduce ? false : { height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={reduce ? undefined : { height: 0, opacity: 0 }}
                      transition={{ duration: 0.22 }}
                    >
                      <NavLink
                        to="/product"
                        onClick={() => {
                          goTop();
                          setOpen(false);
                        }}
                      >
                        <QrifyMark />
                      </NavLink>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </motion.div>
              <PrimaryButton to="/contact" onClick={() => setOpen(false)}>
                Contact us
              </PrimaryButton>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  );
};

export default SiteNav;
